import { useEffect, useMemo, useState } from "react";
import type { FormField } from "../components/AdminDataModal";
import { useMessageModal } from "../contexts/MessageModalContext";
import { useFormat } from "../../context/FormatContext";
import AdminListToolkit from "../components/listing/AdminListToolkit";
import AdminDataModal from "../components/AdminDataModal";
import DataTable from "../components/DataTable";
import type { Column } from "../components/DataTable";
import "../styles/adminPayments.css";

interface MarketTopupRow {
  id: number;
  paymentRef: string;
  subscriberId: number;
  planId: number;
  buyerName: string;
  buyerEmail: string;
  buyerPhone: string;
  amount: number;
  paidAmount: number | null;
  exchangeRate: number | null;
  searchChargeAmount: number;
  paymentCurrency: string;
  title: string;
  description: string | null;
  paymentMethod: string;
  paymentProvider: "SecurePay" | "Stripe";
  status: "Pending" | "Success" | "Failed" | "Cancelled";
  securepayTransactionId: string | null;
  stripeSessionId: string | null;
  stripePaymentIntentId: string | null;
  securepayRawResponse: string | null;
  stripeRawResponse: string | null;
  walletCredited: number;
  createdAt: string;
  updatedAt: string;
  subscriberName?: string | null;
  subscriberEmail?: string | null;
  subscriberPhone?: string | null;
}

export default function AdminMarketAnalysisTopups() {
  const BASE = import.meta.env.VITE_LYRAZONE_BASE_URL;
  const { showModal } = useMessageModal();
  const { formatDateToDMY, formatCurrency } = useFormat();

  const [data, setData] = useState<MarketTopupRow[]>([]);
  const [selectedRow, setSelectedRow] = useState<MarketTopupRow | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"view" | "edit">("view");
  const [activeRow, setActiveRow] = useState<MarketTopupRow | null>(null);
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});

  const toLabel = (value: string | number | null | undefined) =>
    String(value ?? "")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());

  const topupFields: FormField[] = useMemo(
    () => [
      { key: "id", label: "Topup Row ID", colSpan: 1 },
      { key: "paymentRef", label: "Payment Ref", colSpan: 1 },
      { key: "subscriberId", label: "Subscriber ID", colSpan: 1 },
      { key: "planId", label: "Plan ID", colSpan: 1 },

      { key: "buyerName", label: "Buyer Name", colSpan: 1 },
      { key: "buyerEmail", label: "Buyer Email", colSpan: 1 },
      { key: "buyerPhone", label: "Buyer Phone", colSpan: 1 },

      { key: "title", label: "Plan Title", colSpan: 1 },
      { key: "amount", label: "Wallet Credit Amount", colSpan: 1 },
      { key: "paidAmount", label: "Paid Amount", colSpan: 1 },
      { key: "exchangeRate", label: "Exchange Rate", colSpan: 1 },
      { key: "searchChargeAmount", label: "Search Charge Amount", colSpan: 1 },
      { key: "paymentCurrency", label: "Payment Currency", colSpan: 1 },

      {
        key: "paymentProvider",
        label: "Provider",
        type: "select",
        options: [
          { label: "SecurePay", value: "SecurePay" },
          { label: "Stripe", value: "Stripe" },
        ],
        colSpan: 1,
      },
      {
        key: "paymentMethod",
        label: "Payment Method",
        colSpan: 1,
      },
      {
        key: "status",
        label: "Status",
        type: "select",
        options: [
          { label: "Pending", value: "Pending" },
          { label: "Success", value: "Success" },
          { label: "Failed", value: "Failed" },
          { label: "Cancelled", value: "Cancelled" },
        ],
        colSpan: 1,
      },
      {
        key: "walletCredited",
        label: "Wallet Credited",
        type: "select",
        options: [
          { label: "No", value: "0" },
          { label: "Yes", value: "1" },
        ],
        colSpan: 1,
      },

      {
        key: "securepayTransactionId",
        label: "SecurePay Transaction ID",
        colSpan: 1,
      },
      { key: "stripeSessionId", label: "Stripe Session ID", colSpan: 1 },
      {
        key: "stripePaymentIntentId",
        label: "Stripe Payment Intent ID",
        colSpan: 1,
      },

      { key: "subscriberName", label: "Subscriber Name", colSpan: 1 },
      { key: "subscriberEmail", label: "Subscriber Email", colSpan: 1 },
      { key: "subscriberPhone", label: "Subscriber Phone", colSpan: 1 },

      { key: "createdAt", label: "Created At", colSpan: 1 },
      { key: "updatedAt", label: "Updated At", colSpan: 1 },

      {
        key: "description",
        label: "Description",
        type: "textarea",
        colSpan: 2,
      },
      {
        key: "securepayRawResponse",
        label: "SecurePay Raw Response",
        type: "textarea",
        colSpan: 2,
      },
      {
        key: "stripeRawResponse",
        label: "Stripe Raw Response",
        type: "textarea",
        colSpan: 2,
      },
    ],
    [],
  );

  const fetchTopups = async () => {
    try {
      const res = await fetch(`${BASE}/admin/market-analysis-topups`, {
        credentials: "include",
      });

      const result = await res.json();

      if (result?.success) {
        setData(result.data || []);
      } else {
        setData([]);
      }
    } catch (error) {
      console.error("fetchTopups error:", error);
      setData([]);
    }
  };

  useEffect(() => {
    fetchTopups();
  }, []);

  const toggleExpanded = (id: number) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const renderStatusBadge = (status: string | null) => {
    const normalized = String(status || "").toLowerCase();

    const className =
      normalized === "success"
        ? "badge-success"
        : normalized === "failed" || normalized === "cancelled"
          ? "badge-danger"
          : normalized === "pending"
            ? "badge-warning"
            : "badge-secondary";

    return <span className={className}>{toLabel(status || "-")}</span>;
  };

  const renderWalletBadge = (value: number) => {
    return Number(value) === 1 ? (
      <span className="badge-success">Credited</span>
    ) : (
      <span className="badge-warning">Not Credited</span>
    );
  };

  const buildModalRowData = (row: MarketTopupRow) => ({
    ...row,
    amount: formatCurrency(row.amount),
    paidAmount: `${row.paymentCurrency || "MYR"} ${Number(
      row.paidAmount || row.amount || 0,
    ).toLocaleString("en-MY", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`,
    exchangeRate: Number(row.exchangeRate || 1).toFixed(6),
    searchChargeAmount: formatCurrency(row.searchChargeAmount),
    walletCredited: String(row.walletCredited ?? 0),
    createdAt: row.createdAt ? new Date(row.createdAt).toLocaleString() : "",
    updatedAt: row.updatedAt ? new Date(row.updatedAt).toLocaleString() : "",
    securepayRawResponse:
      typeof row.securepayRawResponse === "string"
        ? row.securepayRawResponse
        : JSON.stringify(row.securepayRawResponse ?? "", null, 2),
    stripeRawResponse:
      typeof row.stripeRawResponse === "string"
        ? row.stripeRawResponse
        : JSON.stringify(row.stripeRawResponse ?? "", null, 2),
  });

  const handleView = (row: MarketTopupRow) => {
    setModalMode("view");
    setActiveRow(row);
    setModalOpen(true);
  };

  const handleEdit = (row: MarketTopupRow) => {
    setModalMode("edit");
    setActiveRow(row);
    setModalOpen(true);
  };

  const handleSubmit = async (formData: any) => {
    if (!activeRow) return;

    try {
      const res = await fetch(
        `${BASE}/admin/market-analysis-topups/${activeRow.id}`,
        {
          method: "PATCH",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            status: formData.status,
            paymentMethod: formData.paymentMethod,
            walletCredited: Number(formData.walletCredited),
          }),
        },
      );

      const result = await res.json();

      if (result.success) {
        await fetchTopups();
        setModalOpen(false);
        setActiveRow(null);

        showModal({
          type: "success",
          title: "Success",
          message: "Market Analysis topup updated successfully.",
        });
      } else {
        showModal({
          type: "error",
          title: "Error",
          message: result.message || "Unable to update topup payment.",
        });
      }
    } catch (error) {
      console.error("handleSubmit error:", error);

      showModal({
        type: "error",
        title: "Error",
        message: "Unable to update topup payment.",
      });
    }
  };

  const topupTableRows = useMemo(
    () =>
      data.map((row) => ({
        ...row,
        amountDisplay: formatCurrency(row.amount),
        paidAmountDisplay: `${row.paymentCurrency || "MYR"} ${Number(
          row.paidAmount || row.amount || 0,
        ).toLocaleString("en-MY", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
        searchChargeDisplay: formatCurrency(row.searchChargeAmount),
        createdAtDisplay: row.createdAt ? formatDateToDMY(row.createdAt) : "",
        subscriberDisplay:
          row.subscriberName || row.buyerName || `Subscriber #${row.subscriberId}`,
      })),
    [data, formatCurrency, formatDateToDMY],
  );

  const topupSearchColumns: Column[] = [
    { key: "paymentRef", label: "Payment Ref" },
    { key: "subscriberDisplay", label: "Subscriber" },
    { key: "buyerName", label: "Buyer Name" },
    { key: "buyerEmail", label: "Buyer Email" },
    { key: "title", label: "Plan" },
    { key: "paymentProvider", label: "Provider" },
    { key: "paymentMethod", label: "Method" },
    { key: "status", label: "Status" },
    { key: "amountDisplay", label: "Wallet Credit" },
    { key: "paidAmountDisplay", label: "Paid Amount" },
    { key: "paymentCurrency", label: "Currency" },
    { key: "createdAtDisplay", label: "Created" },
  ];

  const topupTableColumns: Column[] = [
    {
      key: "expand",
      label: "",
      filterable: false,
      render: (row: MarketTopupRow) => {
        const expanded = !!expandedRows[row.id];

        return (
          <button
            type="button"
            className="admin-payments-expand-btn"
            onClick={(e) => {
              e.stopPropagation();
              toggleExpanded(row.id);
            }}
          >
            {expanded ? "-" : "+"}
          </button>
        );
      },
    },
    { key: "paymentRef", label: "Payment Ref" },
    { key: "subscriberDisplay", label: "Subscriber" },
    { key: "title", label: "Plan" },
    { key: "paymentProvider", label: "Provider" },
    {
      key: "status",
      label: "Status",
      render: (row: MarketTopupRow) => renderStatusBadge(row.status),
    },
    {
      key: "amountDisplay",
      label: "Wallet Credit",
    },
    {
      key: "paidAmountDisplay",
      label: "Paid Amount",
    },
    {
      key: "walletCredited",
      label: "Wallet",
      render: (row: MarketTopupRow) => renderWalletBadge(row.walletCredited),
    },
    {
      key: "createdAtDisplay",
      label: "Created",
    },
  ];

  return (
    <div>
      <AdminListToolkit
        title="Market Analysis Topups"
        columns={topupSearchColumns}
        data={topupTableRows}
        selectedRow={selectedRow}
        onView={(row) => handleView(row as MarketTopupRow)}
        onEdit={(row) => handleEdit(row as MarketTopupRow)}
        enableExport
        exportFileName="market-analysis-topups"
      >
        {(filteredData: MarketTopupRow[]) => (
          <div className="admin-payments-table-wrap">
            <DataTable
              columns={topupTableColumns}
              data={filteredData}
              selectedRowId={selectedRow?.id}
              onRowSelect={(row) => setSelectedRow(row as MarketTopupRow)}
              getRowKey={(row) => row.id}
              getRowClassName={(row) =>
                selectedRow?.id === row.id
                  ? "admin-payments-parent-row selected"
                  : "admin-payments-parent-row"
              }
              isRowExpanded={(row) => !!expandedRows[row.id]}
              expandedRowClassName="admin-payments-children-row"
              renderExpandedRow={(row: MarketTopupRow) => (
                <div className="admin-payments-children-box">
                  <div className="admin-payments-summary">
                    <h4>Payment Summary</h4>

                    <p>
                      <strong>Payment Ref:</strong> {row.paymentRef || "-"}
                    </p>

                    <p>
                      <strong>Provider:</strong> {row.paymentProvider || "-"}
                    </p>

                    <p>
                      <strong>Method:</strong> {row.paymentMethod || "-"}
                    </p>

                    <p>
                      <strong>Status:</strong> {toLabel(row.status || "-")}
                    </p>

                    <p>
                      <strong>Wallet Credit:</strong>{" "}
                      {formatCurrency(row.amount)}
                    </p>

                    <p>
                      <strong>Paid Amount:</strong>{" "}
                      {row.paymentCurrency || "MYR"}{" "}
                      {Number(row.paidAmount || row.amount || 0).toLocaleString(
                        "en-MY",
                        {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        },
                      )}
                    </p>

                    <p>
                      <strong>Exchange Rate:</strong>{" "}
                      {Number(row.exchangeRate || 1).toFixed(6)}
                    </p>

                    <p>
                      <strong>Wallet Credited:</strong>{" "}
                      {Number(row.walletCredited) === 1 ? "Yes" : "No"}
                    </p>
                  </div>

                  <div className="admin-payments-order-box">
                    <h4>Subscriber / Buyer</h4>

                    <p>
                      <strong>Subscriber:</strong>{" "}
                      {row.subscriberName || `#${row.subscriberId}`}
                    </p>

                    <p>
                      <strong>Subscriber Email:</strong>{" "}
                      {row.subscriberEmail || "-"}
                    </p>

                    <p>
                      <strong>Buyer Name:</strong> {row.buyerName || "-"}
                    </p>

                    <p>
                      <strong>Buyer Email:</strong> {row.buyerEmail || "-"}
                    </p>

                    <p>
                      <strong>Buyer Phone:</strong> {row.buyerPhone || "-"}
                    </p>
                  </div>

                  <div className="admin-payments-payload-box">
                    <h4>Gateway Reference</h4>

                    <p>
                      <strong>SecurePay Txn:</strong>{" "}
                      {row.securepayTransactionId || "-"}
                    </p>

                    <p>
                      <strong>Stripe Session:</strong>{" "}
                      {row.stripeSessionId || "-"}
                    </p>

                    <p>
                      <strong>Stripe Intent:</strong>{" "}
                      {row.stripePaymentIntentId || "-"}
                    </p>

                    <h4>Raw Response</h4>

                    <pre>
                      {row.paymentProvider === "Stripe"
                        ? row.stripeRawResponse || "-"
                        : row.securepayRawResponse || "-"}
                    </pre>
                  </div>
                </div>
              )}
            />
          </div>
        )}
      </AdminListToolkit>

      <AdminDataModal
        isOpen={modalOpen}
        mode={modalMode}
        title="Market Analysis Topup"
        fields={topupFields}
        rowData={activeRow ? buildModalRowData(activeRow) : null}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
        size="xl"
      />
    </div>
  );
}